/**
 * Utility for handling initData refresh and validation errors
 */

export interface InitDataError {
  error: string
  debug?: Record<string, any>
}

export class InitDataManager {
  private static instance: InitDataManager
  private refreshCallbacks: Set<() => void> = new Set()
  
  static getInstance(): InitDataManager {
    if (!InitDataManager.instance) {
      InitDataManager.instance = new InitDataManager()
    }
    return InitDataManager.instance
  }

  /**
   * Check if an error is related to initData validation
   */
  isInitDataError(error: any): error is InitDataError {
    if (typeof error === 'object' && error?.error) {
      const errorCode = error.error
      return [
        'INITDATA_MISSING_AUTH_DATE',
        'INITDATA_FUTURE', 
        'INITDATA_EXPIRED_RESTART_APP',
        'INVALID_INITDATA_SIGNATURE',
        'INVALID_INITDATA_USER_MISMATCH',
        'INVALID_INITDATA' // legacy error
      ].includes(errorCode)
    }
    return false
  }

  /**
   * Check if initData is expired and needs refresh
   */
  isExpiredError(error: any): boolean {
    return error?.error === 'INITDATA_EXPIRED_RESTART_APP'
  }

  /**
   * Check if there's a user mismatch (viewing another user's profile)
   */
  isUserMismatchError(error: any): boolean {
    return error?.error === 'INVALID_INITDATA_USER_MISMATCH'
  }

  /**
   * Get current initData age in seconds
   */
  getInitDataAge(): number | null {
    if (typeof window === 'undefined' || !window.Telegram?.WebApp?.initData) {
      return null
    }

    try {
      const params = new URLSearchParams(window.Telegram.WebApp.initData)
      const authDate = Number(params.get('auth_date') || 0)
      if (!authDate) return null
      
      return Math.floor(Date.now() / 1000) - authDate
    } catch {
      return null
    }
  }

  /**
   * Check if initData is close to expiring (within 5 minutes)
   */
  isInitDataStale(maxAge: number = 3600): boolean {
    const age = this.getInitDataAge()
    if (age === null) return false
    
    // Consider stale if within 5 minutes of expiring
    return age > (maxAge - 300)
  }

  /**
   * Attempt to refresh the app to get new initData
   */
  refreshApp(): void {
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      // Notify callbacks before refresh
      this.refreshCallbacks.forEach(callback => {
        try {
          callback()
        } catch (e) {
          console.warn('Error in refresh callback:', e)
        }
      })

      // Use Telegram's built-in refresh if available
      if (window.Telegram.WebApp.relaunch) {
        window.Telegram.WebApp.relaunch()
      } else {
        // Fallback to page reload
        window.location.reload()
      }
    }
  }

  /**
   * Register a callback to be called before app refresh
   */
  onBeforeRefresh(callback: () => void): () => void {
    this.refreshCallbacks.add(callback)
    return () => this.refreshCallbacks.delete(callback)
  }

  /**
   * Get user-friendly error message
   */
  getErrorMessage(error: any): string {
    if (!this.isInitDataError(error)) {
      return 'An unexpected error occurred'
    }

    switch (error.error) {
      case 'INITDATA_EXPIRED_RESTART_APP':
        return 'Your session has expired. Please restart the app.'
      case 'INVALID_INITDATA_USER_MISMATCH':
        return 'Cannot view this profile. Please try again.'
      case 'INVALID_INITDATA_SIGNATURE':
        return 'Authentication failed. Please restart the app.'
      case 'INITDATA_FUTURE':
        return 'Clock synchronization error. Please try again.'
      case 'INITDATA_MISSING_AUTH_DATE':
        return 'Invalid session data. Please restart the app.'
      default:
        return 'Authentication error. Please restart the app.'
    }
  }

  /**
   * Handle initData error with appropriate action
   */
  handleInitDataError(error: any): boolean {
    if (!this.isInitDataError(error)) {
      return false
    }

    console.warn('InitData error:', error)

    // For expired errors, offer to refresh
    if (this.isExpiredError(error)) {
      if (typeof window !== 'undefined' && window.confirm) {
        const shouldRefresh = window.confirm(
          'Your session has expired. Would you like to restart the app?'
        )
        if (shouldRefresh) {
          this.refreshApp()
          return true
        }
      }
    }

    // For user mismatch, just show the error (don't refresh)
    if (this.isUserMismatchError(error)) {
      return true // Error handled, don't propagate
    }

    return true // Error handled
  }
}

export const initDataManager = InitDataManager.getInstance()